import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { 
  ArrowLeft,
  Plus, 
  Loader2,
  FolderKanban,
  Pencil,
  Calendar as CalendarIcon,
  DollarSign,
  Target,
  TrendingUp,
  Link,
  Globe,
  BarChart3,
  FileText,
  Clock,
  CheckCircle2,
  Circle,
  AlertCircle,
  Flag,
  UserPlus,
  Trash2,
  ListTodo,
  MessageSquare,
  Send,
  X,
  ExternalLink,
  Monitor,
  User,
  Lock
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import SEOAuditBot from '../components/SEOAuditBot';
import MediaBrowser from '../components/MediaBrowser';
import ProjectAnalytics from '../components/ProjectAnalytics';
import SecureLoginsFolder from '../components/SecureLoginsFolder';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' }
];

const projectTypes = [
  { value: 'web_design', label: 'Web Design' },
  { value: 'seo', label: 'SEO' },
  { value: 'branding', label: 'Branding' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'software_development', label: 'Software Development' },
  { value: 'other', label: 'Other' }
];

// Project type specific field templates
const projectTemplates = {
  web_design: {
    fields: [
      { name: 'num_pages', label: 'Number of Pages', type: 'number', placeholder: 'e.g., 5' },
      { name: 'cms_platform', label: 'CMS Platform', type: 'select', options: ['WordPress', 'Webflow', 'Shopify', 'Custom', 'None'] },
      { name: 'responsive', label: 'Responsive Design', type: 'boolean', default: true },
      { name: 'design_style', label: 'Design Style', type: 'select', options: ['Modern', 'Minimalist', 'Corporate', 'Creative', 'E-commerce'] },
      { name: 'has_ecommerce', label: 'E-commerce Features', type: 'boolean', default: false },
      { name: 'hosting_provider', label: 'Hosting Provider', type: 'text', placeholder: 'e.g., AWS, GoDaddy' },
    ]
  },
  seo: {
    fields: [
      { name: 'target_keywords', label: 'Target Keywords', type: 'textarea', placeholder: 'Enter primary keywords (comma separated)' },
      { name: 'competitor_urls', label: 'Competitor URLs', type: 'textarea', placeholder: 'Enter competitor URLs (one per line)' },
      { name: 'monthly_traffic_goal', label: 'Monthly Traffic Goal', type: 'number', placeholder: 'e.g., 10000' },
      { name: 'current_ranking', label: 'Current Google Ranking', type: 'text', placeholder: 'e.g., Page 2-3' },
      { name: 'local_seo', label: 'Local SEO Focus', type: 'boolean', default: false },
      { name: 'content_strategy', label: 'Content Strategy', type: 'select', options: ['Blog Posts', 'Landing Pages', 'Product Pages', 'All'] },
    ]
  },
  software_development: {
    fields: [
      { name: 'tech_stack', label: 'Tech Stack', type: 'text', placeholder: 'e.g., React, Node.js, PostgreSQL' },
      { name: 'repository_url', label: 'Repository URL', type: 'text', placeholder: 'https://github.com/...' },
      { name: 'deployment_env', label: 'Deployment Environment', type: 'select', options: ['AWS', 'Azure', 'GCP', 'Heroku', 'Vercel', 'Self-hosted'] },
      { name: 'sprint_duration', label: 'Sprint Duration (weeks)', type: 'select', options: ['1', '2', '3', '4'] },
      { name: 'has_api', label: 'API Development', type: 'boolean', default: true },
      { name: 'testing_required', label: 'Testing Requirements', type: 'select', options: ['Unit Tests', 'Integration Tests', 'E2E Tests', 'All', 'None'] },
    ]
  },
  branding: {
    fields: [
      { name: 'brand_type', label: 'Brand Type', type: 'select', options: ['New Brand', 'Rebrand', 'Brand Refresh', 'Brand Extension'] },
      { name: 'deliverables', label: 'Deliverables', type: 'textarea', placeholder: 'Logo, Business Cards, Brand Guidelines...' },
      { name: 'industry', label: 'Industry', type: 'text', placeholder: 'e.g., Technology, Healthcare' },
      { name: 'target_audience', label: 'Target Audience', type: 'text', placeholder: 'e.g., Young professionals 25-40' },
      { name: 'color_preferences', label: 'Color Preferences', type: 'text', placeholder: 'e.g., Blue, Professional' },
      { name: 'competitors_ref', label: 'Competitor References', type: 'textarea', placeholder: 'Brands to reference or avoid' },
    ]
  },
  marketing: {
    fields: [
      { name: 'campaign_type', label: 'Campaign Type', type: 'select', options: ['Social Media', 'Email', 'PPC', 'Content Marketing', 'Full Funnel'] },
      { name: 'platforms', label: 'Platforms', type: 'text', placeholder: 'e.g., Facebook, Instagram, Google Ads' },
      { name: 'monthly_budget', label: 'Monthly Ad Budget ($)', type: 'number', placeholder: 'e.g., 5000' },
      { name: 'target_cpa', label: 'Target CPA ($)', type: 'number', placeholder: 'e.g., 50' },
      { name: 'conversion_goal', label: 'Conversion Goal', type: 'select', options: ['Lead Generation', 'Sales', 'App Installs', 'Brand Awareness'] },
      { name: 'reporting_frequency', label: 'Reporting Frequency', type: 'select', options: ['Weekly', 'Bi-weekly', 'Monthly'] },
    ]
  },
  other: {
    fields: [
      { name: 'project_scope', label: 'Project Scope', type: 'textarea', placeholder: 'Describe the project scope...' },
      { name: 'key_deliverables', label: 'Key Deliverables', type: 'textarea', placeholder: 'List main deliverables...' },
      { name: 'special_requirements', label: 'Special Requirements', type: 'textarea', placeholder: 'Any special requirements...' },
    ]
  }
};

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  const isClientTeam = user?.role === 'client_team';
  const canDelete = !isClientTeam && user?.role !== 'client';  // client_team and client cannot delete
  const canEditCosts = !isClientTeam && user?.role !== 'client';  // Only admin/team can manage costs
  const [project, setProject] = useState(null);
  const [client, setClient] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [seoData, setSeoData] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isAddSeoOpen, setIsAddSeoOpen] = useState(false);
  const [dueDate, setDueDate] = useState(null);
  
  // Costs state
  const [costs, setCosts] = useState([]);
  const [costsSummary, setCostsSummary] = useState(null);
  const [isAddCostOpen, setIsAddCostOpen] = useState(false);
  const [isEditCostOpen, setIsEditCostOpen] = useState(false);
  const [selectedCost, setSelectedCost] = useState(null);
  const [costForm, setCostForm] = useState({
    name: '',
    description: '',
    amount: '',
    frequency: 'monthly',
    category: 'other'
  });
  
  // Task state
  const [tasks, setTasks] = useState([]);
  const [isAddTaskOpen, setIsAddTaskOpen] = useState(false);
  const [isEditTaskOpen, setIsEditTaskOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskComments, setTaskComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [taskDueDate, setTaskDueDate] = useState(null);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    assigned_to: [],  // Changed to array for multi-select
    priority: 'medium',
    is_milestone: false
  });
  
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    status: 'not_started',
    project_type: 'web_design',
    budget: '',
    website_url: '',
    dev_site_url: '',
    custom_fields: {}
  });
  const [isDevSiteOpen, setIsDevSiteOpen] = useState(false);
  const [devSiteUrl, setDevSiteUrl] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [seoForm, setSeoForm] = useState({
    keywords: '',
    domain_authority: '',
    page_authority: '',
    organic_traffic: '',
    backlinks: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchAssignableUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/projects/${projectId}/assignable-users`, getAuthHeaders());
      setAssignableUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch assignable users');
    }
  };

  const fetchTasks = async () => {
    try {
      const res = await axios.get(`${API_URL}/tasks?project_id=${projectId}`, getAuthHeaders());
      setTasks(res.data);
    } catch (error) {
      console.error('Failed to fetch tasks');
    }
  };

  const fetchCosts = async () => {
    try {
      const [costsRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/project-costs?project_id=${projectId}`, getAuthHeaders()),
        axios.get(`${API_URL}/project-costs/summary/${projectId}`, getAuthHeaders())
      ]);
      setCosts(costsRes.data);
      setCostsSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch costs');
    }
  };

  const fetchData = async () => {
    try {
      const projectRes = await axios.get(`${API_URL}/projects/${projectId}`, getAuthHeaders());
      setProject(projectRes.data);
      setProjectForm({
        name: projectRes.data.name,
        description: projectRes.data.description || '',
        status: projectRes.data.status,
        project_type: projectRes.data.project_type,
        budget: projectRes.data.budget || '',
        website_url: projectRes.data.website_url || '',
        dev_site_url: projectRes.data.dev_site_url || '',
        custom_fields: projectRes.data.custom_fields || {}
      });
      
      // Fetch assignable users for this project
      fetchAssignableUsers();
      setDueDate(projectRes.data.due_date ? new Date(projectRes.data.due_date) : null);

      // Fetch related data
      const [clientRes, seoRes, invoicesRes, tenantRes, tasksRes, costsRes, costsSummaryRes] = await Promise.all([
        axios.get(`${API_URL}/clients/${projectRes.data.client_id}`, getAuthHeaders()),
        axios.get(`${API_URL}/seo-data?project_id=${projectId}`, getAuthHeaders()),
        axios.get(`${API_URL}/invoices?client_id=${projectRes.data.client_id}`, getAuthHeaders()),
        axios.get(`${API_URL}/tenants/${projectRes.data.organization_id}`, getAuthHeaders()).catch(() => ({ data: null })),
        axios.get(`${API_URL}/tasks?project_id=${projectId}`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/project-costs?project_id=${projectId}`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/project-costs/summary/${projectId}`, getAuthHeaders()).catch(() => ({ data: null }))
      ]);
      
      setClient(clientRes.data);
      setSeoData(seoRes.data);
      setInvoices(invoicesRes.data.filter(i => i.project_id === projectId));
      setTenant(tenantRes.data);
      setTasks(tasksRes.data || []);
      setCosts(costsRes.data || []);
      setCostsSummary(costsSummaryRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/projects/${projectId}`, {
        ...projectForm,
        due_date: dueDate ? format(dueDate, 'yyyy-MM-dd') : null,
        budget: projectForm.budget ? parseFloat(projectForm.budget) : null,
        custom_fields: projectForm.custom_fields
      }, getAuthHeaders());
      toast.success('Project updated successfully');
      setIsEditOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update project');
    }
  };

  // Update custom field value
  const updateCustomField = (fieldName, value) => {
    setProjectForm(prev => ({
      ...prev,
      custom_fields: {
        ...prev.custom_fields,
        [fieldName]: value
      }
    }));
  };

  // Render custom fields based on project type
  const renderEditCustomFields = () => {
    const template = projectTemplates[projectForm.project_type];
    if (!template) return null;

    const typeInfo = projectTypes.find(t => t.value === projectForm.project_type);

    return (
      <div className="space-y-4 pt-4 border-t border-slate-100">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          {typeInfo?.label || 'Project'} Details
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {template.fields.map(field => (
            <div key={field.name} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
              {field.type === 'boolean' ? (
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <Label htmlFor={`edit-${field.name}`} className="cursor-pointer">{field.label}</Label>
                  <Switch
                    id={`edit-${field.name}`}
                    checked={projectForm.custom_fields?.[field.name] || false}
                    onCheckedChange={(checked) => updateCustomField(field.name, checked)}
                  />
                </div>
              ) : field.type === 'select' ? (
                <div className="space-y-2">
                  <Label>{field.label}</Label>
                  <Select
                    value={projectForm.custom_fields?.[field.name] || ''}
                    onValueChange={(value) => updateCustomField(field.name, value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {field.options.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : field.type === 'textarea' ? (
                <div className="space-y-2">
                  <Label>{field.label}</Label>
                  <Textarea
                    value={projectForm.custom_fields?.[field.name] || ''}
                    onChange={(e) => updateCustomField(field.name, e.target.value)}
                    placeholder={field.placeholder}
                    rows={2}
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>{field.label}</Label>
                  <Input
                    type={field.type}
                    value={projectForm.custom_fields?.[field.name] || ''}
                    onChange={(e) => updateCustomField(field.name, e.target.value)}
                    placeholder={field.placeholder}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleAddSeoData = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/seo-data`, {
        project_id: projectId,
        keywords: seoForm.keywords.split(',').map(k => k.trim()).filter(k => k),
        domain_authority: seoForm.domain_authority ? parseFloat(seoForm.domain_authority) : null,
        page_authority: seoForm.page_authority ? parseFloat(seoForm.page_authority) : null,
        organic_traffic: seoForm.organic_traffic ? parseInt(seoForm.organic_traffic) : null,
        backlinks: seoForm.backlinks ? parseInt(seoForm.backlinks) : null,
        notes: seoForm.notes
      }, getAuthHeaders());
      toast.success('SEO data added');
      setIsAddSeoOpen(false);
      setSeoForm({ keywords: '', domain_authority: '', page_authority: '', organic_traffic: '', backlinks: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add SEO data');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API_URL}/projects/${projectId}`, { status: newStatus }, getAuthHeaders());
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Task handlers
  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/tasks`, {
        ...taskForm,
        project_id: projectId,
        due_date: taskDueDate ? format(taskDueDate, 'yyyy-MM-dd') : null,
        assigned_to: taskForm.assigned_to.length > 0 ? taskForm.assigned_to : []
      }, getAuthHeaders());
      toast.success('Task created');
      setIsAddTaskOpen(false);
      setTaskForm({ title: '', description: '', assigned_to: [], priority: 'medium', is_milestone: false });
      setTaskDueDate(null);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setTaskForm({
      title: task.title,
      description: task.description || '',
      assigned_to: task.assigned_to || [],
      priority: task.priority,
      is_milestone: task.is_milestone
    });
    setTaskDueDate(task.due_date ? new Date(task.due_date) : null);
    fetchTaskComments(task.id);
    setIsEditTaskOpen(true);
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    if (!selectedTask) return;
    try {
      await axios.put(`${API_URL}/tasks/${selectedTask.id}`, {
        ...taskForm,
        due_date: taskDueDate ? format(taskDueDate, 'yyyy-MM-dd') : null,
        assigned_to: taskForm.assigned_to.length > 0 ? taskForm.assigned_to : []
      }, getAuthHeaders());
      toast.success('Task updated');
      setIsEditTaskOpen(false);
      setSelectedTask(null);
      setTaskForm({ title: '', description: '', assigned_to: [], priority: 'medium', is_milestone: false });
      setTaskDueDate(null);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  const toggleAssignee = (userId) => {
    setTaskForm(prev => {
      const currentAssigned = prev.assigned_to || [];
      if (currentAssigned.includes(userId)) {
        return { ...prev, assigned_to: currentAssigned.filter(id => id !== userId) };
      } else {
        return { ...prev, assigned_to: [...currentAssigned, userId] };
      }
    });
  };

  const fetchTaskComments = async (taskId) => {
    try {
      const res = await axios.get(`${API_URL}/tasks/${taskId}/comments`, getAuthHeaders());
      setTaskComments(res.data);
    } catch (error) {
      console.error('Failed to fetch comments');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTask) return;
    try {
      await axios.post(`${API_URL}/tasks/${selectedTask.id}/comments`, { content: newComment }, getAuthHeaders());
      setNewComment('');
      fetchTaskComments(selectedTask.id);
      fetchTasks(); // refresh comment count
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!selectedTask) return;
    try {
      await axios.delete(`${API_URL}/tasks/${selectedTask.id}/comments/${commentId}`, getAuthHeaders());
      fetchTaskComments(selectedTask.id);
      fetchTasks();
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const handleToggleTaskStatus = async (task) => {
    const newStatus = task.status === 'completed' ? 'todo' : 'completed';
    try {
      await axios.put(`${API_URL}/tasks/${task.id}`, { status: newStatus }, getAuthHeaders());
      toast.success(newStatus === 'completed' ? 'Task completed!' : 'Task reopened');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axios.delete(`${API_URL}/tasks/${taskId}`, getAuthHeaders());
      toast.success('Task deleted');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleToggleAssignee = async (taskId, userId, currentAssigned) => {
    const isAssigned = currentAssigned.includes(userId);
    const newAssigned = isAssigned 
      ? currentAssigned.filter(id => id !== userId)
      : [...currentAssigned, userId];
    
    try {
      await axios.put(`${API_URL}/tasks/${taskId}`, { assigned_to: newAssigned }, getAuthHeaders());
      toast.success(isAssigned ? 'Assignee removed' : 'Assignee added');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to update assignees');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-50';
      case 'high': return 'text-orange-600 bg-orange-50';
      case 'medium': return 'text-blue-600 bg-blue-50';
      case 'low': return 'text-slate-500 bg-slate-50';
      default: return 'text-slate-500 bg-slate-50';
    }
  };

  // Cost handlers
  const handleAddCost = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/project-costs`, {
        project_id: projectId,
        name: costForm.name,
        description: costForm.description || null,
        amount: parseFloat(costForm.amount),
        frequency: costForm.frequency,
        category: costForm.category
      }, getAuthHeaders());
      toast.success('Cost added');
      setIsAddCostOpen(false);
      setCostForm({ name: '', description: '', amount: '', frequency: 'monthly', category: 'other' });
      fetchCosts();
    } catch (error) {
      toast.error('Failed to add cost');
    }
  };

  const handleUpdateCost = async (e) => {
    e.preventDefault();
    if (!selectedCost) return;
    try {
      await axios.put(`${API_URL}/project-costs/${selectedCost.id}`, {
        name: costForm.name,
        description: costForm.description || null,
        amount: parseFloat(costForm.amount),
        frequency: costForm.frequency,
        category: costForm.category
      }, getAuthHeaders());
      toast.success('Cost updated');
      setIsEditCostOpen(false);
      setSelectedCost(null);
      fetchCosts();
    } catch (error) {
      toast.error('Failed to update cost');
    }
  };

  const handleDeleteCost = async (costId) => {
    if (!window.confirm('Delete this cost item?')) return;
    try {
      await axios.delete(`${API_URL}/project-costs/${costId}`, getAuthHeaders());
      toast.success('Cost deleted');
      fetchCosts();
    } catch (error) {
      toast.error('Failed to delete cost');
    }
  };

  const openEditCost = (cost) => {
    setSelectedCost(cost);
    setCostForm({
      name: cost.name,
      description: cost.description || '',
      amount: cost.amount.toString(),
      frequency: cost.frequency,
      category: cost.category
    });
    setIsEditCostOpen(true);
  };

  const getCategoryLabel = (category) => {
    const labels = {
      hosting: 'Hosting',
      api: 'API/Service',
      subscription: 'Subscription',
      credits: 'Credits',
      messaging: 'Messaging',
      other: 'Other'
    };
    return labels[category] || category;
  };

  const getFrequencyLabel = (frequency) => {
    const labels = {
      one_time: 'One-time',
      monthly: 'Monthly',
      yearly: 'Yearly'
    };
    return labels[frequency] || frequency;
  };

  const getCategoryColor = (category) => {
    const colors = {
      hosting: 'bg-blue-100 text-blue-700',
      api: 'bg-purple-100 text-purple-700',
      subscription: 'bg-green-100 text-green-700',
      credits: 'bg-yellow-100 text-yellow-700',
      messaging: 'bg-pink-100 text-pink-700',
      other: 'bg-slate-100 text-slate-700'
    };
    return colors[category] || 'bg-slate-100 text-slate-700';
  };

  const openDevSitePreview = async () => {
    try {
      const res = await axios.get(`${API_URL}/projects/${projectId}/dev-site-url`, getAuthHeaders());
      setDevSiteUrl(res.data.url);
      setIsDevSiteOpen(true);
    } catch (error) {
      toast.error('Development site not available');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-slate-700">Project not found</h2>
        <Button variant="link" onClick={() => navigate('/projects')}>Back to Projects</Button>
      </div>
    );
  }

  // Latest SEO data
  const latestSeo = seoData.length > 0 ? seoData[seoData.length - 1] : null;

  // Chart data
  const chartData = seoData.map((d, i) => ({
    name: `Entry ${i + 1}`,
    da: d.domain_authority || 0,
    pa: d.page_authority || 0,
    traffic: d.organic_traffic || 0,
    backlinks: d.backlinks || 0
  }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="project-detail-page">
      {/* Back button and Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate(-1)}
          data-testid="back-btn"
        >
          <ArrowLeft size={20} />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              {project.name}
            </h1>
            <span className={`status-badge status-${project.status}`}>
              {statusOptions.find(s => s.value === project.status)?.label}
            </span>
          </div>
          <p className="text-slate-500">
            {client?.name} • {projectTypes.find(t => t.value === project.project_type)?.label}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* See Site button - visible to all if dev site exists */}
          {project.has_dev_site && (
            <Button 
              variant="outline" 
              onClick={openDevSitePreview}
              className="bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
              data-testid="see-site-btn"
            >
              <Monitor size={16} className="mr-2" />
              See Development Site
            </Button>
          )}
          <Button 
            variant="outline" 
            onClick={() => setIsEditOpen(true)}
            data-testid="edit-project-btn"
          >
            <Pencil size={16} className="mr-2" />
            Edit Project
          </Button>
        </div>
      </div>

      {/* Website URL banner if set */}
      {project.website_url && (
        <Card className="border-slate-200 shadow-sm bg-gradient-to-r from-slate-50 to-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Globe size={20} className="text-blue-600" />
                <div>
                  <p className="text-xs text-slate-500">Project Live Website</p>
                  <a 
                    href={project.website_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                    data-testid="project-website-link"
                  >
                    {project.website_url}
                  </a>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setActiveTab('seo-audit')}
              >
                SEO Audit
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Clock size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Due Date</p>
                <p className="font-semibold text-slate-900">
                  {project.due_date ? format(new Date(project.due_date), 'MMM d, yyyy') : 'Not set'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <DollarSign size={20} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Budget</p>
                <p className="font-semibold text-slate-900">
                  {project.budget ? `$${project.budget.toLocaleString()}` : 'Not set'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <Globe size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Domain Authority</p>
                <p className="font-semibold text-slate-900">
                  {latestSeo?.domain_authority || 'N/A'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <TrendingUp size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Organic Traffic</p>
                <p className="font-semibold text-slate-900">
                  {latestSeo?.organic_traffic?.toLocaleString() || 'N/A'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Selector */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-slate-700">Quick Status Update:</span>
            <div className="flex gap-2">
              {statusOptions.map((status) => (
                <Button
                  key={status.value}
                  variant={project.status === status.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleStatusChange(status.value)}
                  className={project.status === status.value ? 'bg-slate-900' : ''}
                  data-testid={`status-btn-${status.value}`}
                >
                  {status.label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for different sections */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
          <TabsTrigger value="costs">Costs</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="media">Media</TabsTrigger>
          {(user?.role === 'super_admin' || user?.role === 'admin') && (
            <TabsTrigger value="logins" className="flex items-center gap-1">
              <Lock size={14} />
              Logins
            </TabsTrigger>
          )}
          <TabsTrigger value="seo-audit">SEO Audit</TabsTrigger>
          <TabsTrigger value="seo">SEO Data</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900">Project Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Description</p>
                  <p className="text-slate-700">{project.description || 'No description provided'}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Project Type</p>
                    <p className="font-medium text-slate-900 capitalize">{project.project_type.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Created</p>
                    <p className="font-medium text-slate-900">{format(new Date(project.created_at), 'MMM d, yyyy')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900">Client Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                    <span className="font-semibold text-slate-600">
                      {client?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{client?.name}</p>
                    <p className="text-sm text-slate-500">{client?.company}</p>
                  </div>
                </div>
                {client?.email && <p className="text-sm text-slate-600">{client.email}</p>}
                {client?.phone && <p className="text-sm text-slate-600">{client.phone}</p>}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigate(`/clients/${client?.id}`)}
                  className="mt-2"
                >
                  View Client Profile
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-slate-900">Tasks & Milestones</h3>
            <Button 
              onClick={() => setIsAddTaskOpen(true)} 
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="add-task-btn"
            >
              <Plus size={16} className="mr-2" />
              Add Task
            </Button>
          </div>

          {/* Task Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-4 text-center">
                <ListTodo size={24} className="mx-auto text-slate-600 mb-2" />
                <p className="text-2xl font-bold text-slate-900">{tasks.length}</p>
                <p className="text-xs text-slate-500">Total Tasks</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-4 text-center">
                <Circle size={24} className="mx-auto text-blue-600 mb-2" />
                <p className="text-2xl font-bold text-slate-900">
                  {tasks.filter(t => t.status === 'todo' || t.status === 'in_progress').length}
                </p>
                <p className="text-xs text-slate-500">Active</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-4 text-center">
                <CheckCircle2 size={24} className="mx-auto text-emerald-600 mb-2" />
                <p className="text-2xl font-bold text-slate-900">
                  {tasks.filter(t => t.status === 'completed').length}
                </p>
                <p className="text-xs text-slate-500">Completed</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-4 text-center">
                <Flag size={24} className="mx-auto text-purple-600 mb-2" />
                <p className="text-2xl font-bold text-slate-900">
                  {tasks.filter(t => t.is_milestone).length}
                </p>
                <p className="text-xs text-slate-500">Milestones</p>
              </CardContent>
            </Card>
          </div>

          {/* Task List */}
          {tasks.length > 0 ? (
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-0">
                <ul className="divide-y divide-slate-100">
                  {tasks.map((task) => (
                    <li key={task.id} className="p-4 hover:bg-slate-50 transition-colors">
                      <div className="flex items-start gap-3">
                        {/* Checkbox */}
                        <button
                          onClick={() => handleToggleTaskStatus(task)}
                          className={`mt-1 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            task.status === 'completed' 
                              ? 'bg-emerald-500 border-emerald-500' 
                              : 'border-slate-300 hover:border-slate-400'
                          }`}
                          data-testid={`task-toggle-${task.id}`}
                        >
                          {task.status === 'completed' && (
                            <CheckCircle2 size={14} className="text-white" />
                          )}
                        </button>
                        
                        {/* Task Content */}
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => handleEditTask(task)}>
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className={`font-medium ${task.status === 'completed' ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                              {task.title}
                            </span>
                            {task.is_milestone && (
                              <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                                Milestone
                              </span>
                            )}
                            <span className={`px-2 py-0.5 text-xs rounded-full ${getPriorityColor(task.priority)}`}>
                              {task.priority}
                            </span>
                            {task.comment_count > 0 && (
                              <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-600 rounded-full flex items-center gap-1">
                                <MessageSquare size={10} />
                                {task.comment_count}
                              </span>
                            )}
                          </div>
                          {task.description && (
                            <p className="text-sm text-slate-500 mb-2">{task.description}</p>
                          )}
                          <div className="flex items-center gap-4 text-xs text-slate-400 flex-wrap">
                            {task.due_date && (
                              <span className="flex items-center gap-1">
                                <CalendarIcon size={12} />
                                {format(new Date(task.due_date), 'MMM d, yyyy')}
                              </span>
                            )}
                            {task.assignees && task.assignees.length > 0 && (
                              <span className="flex items-center gap-1">
                                <User size={12} />
                                {task.assignees.map(a => a.name).join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-1">
                          {/* Multi-assign popover */}
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" size="sm" className="h-8 text-xs">
                                <UserPlus size={14} className="mr-1" />
                                {task.assignees?.length || 0}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-56 p-2">
                              <p className="text-xs font-medium text-slate-700 mb-2">Assign to:</p>
                              <div className="space-y-1 max-h-48 overflow-y-auto">
                                {assignableUsers.map(u => (
                                  <div 
                                    key={u.id} 
                                    className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer"
                                    onClick={() => handleToggleAssignee(task.id, u.id, task.assigned_to || [])}
                                  >
                                    <Checkbox 
                                      checked={(task.assigned_to || []).includes(u.id)}
                                      className="pointer-events-none"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm text-slate-800 truncate">{u.name}</p>
                                      <p className="text-xs text-slate-400">{u.type}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-500 hover:text-slate-700"
                            onClick={() => handleEditTask(task)}
                            title="Edit task"
                            data-testid={`edit-task-${task.id}`}
                          >
                            <Pencil size={16} />
                          </Button>
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteTask(task.id)}
                              data-testid={`delete-task-${task.id}`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-12 text-center">
                <ListTodo size={48} className="mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700">No tasks yet</h3>
                <p className="text-slate-500 mt-1">Create tasks and milestones to track project progress</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Costs Tab */}
        <TabsContent value="costs" className="space-y-6">
          {/* Costs Summary Cards */}
          {costsSummary && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                      <DollarSign size={20} className="text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">One-Time Costs</p>
                      <p className="font-semibold text-slate-900">${costsSummary.one_time_total?.toLocaleString() || '0'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                      <Clock size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Monthly Costs</p>
                      <p className="font-semibold text-slate-900">${costsSummary.monthly_total?.toLocaleString() || '0'}/mo</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                      <CalendarIcon size={20} className="text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Yearly Costs</p>
                      <p className="font-semibold text-slate-900">${costsSummary.yearly_total?.toLocaleString() || '0'}/yr</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200 shadow-sm bg-slate-900">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                      <TrendingUp size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Est. Yearly Total</p>
                      <p className="font-semibold text-white">${costsSummary.estimated_yearly?.toLocaleString() || '0'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Costs List */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-slate-900">Project Running Costs</CardTitle>
              {canEditCosts && (
                <Button onClick={() => setIsAddCostOpen(true)} size="sm" className="bg-slate-900 hover:bg-slate-800">
                  <Plus size={16} className="mr-1" />
                  Add Cost
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {costs.length > 0 ? (
                <div className="space-y-3">
                  {costs.map((cost) => (
                    <div 
                      key={cost.id} 
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-100"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                          <DollarSign size={18} className="text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{cost.name}</p>
                          {cost.description && (
                            <p className="text-sm text-slate-500 mt-0.5">{cost.description}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryColor(cost.category)}`}>
                              {getCategoryLabel(cost.category)}
                            </span>
                            <span className="text-xs text-slate-400">
                              {getFrequencyLabel(cost.frequency)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <p className="font-semibold text-lg text-slate-900">
                          ${cost.amount.toLocaleString()}
                          {cost.frequency !== 'one_time' && (
                            <span className="text-sm font-normal text-slate-500">
                              /{cost.frequency === 'monthly' ? 'mo' : 'yr'}
                            </span>
                          )}
                        </p>
                        {canEditCosts && (
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openEditCost(cost)}
                            >
                              <Pencil size={14} />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => handleDeleteCost(cost.id)}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <DollarSign size={40} className="mx-auto text-slate-300 mb-3" />
                  <h3 className="text-lg font-medium text-slate-700">No costs added yet</h3>
                  <p className="text-slate-500 mt-1">Add running costs like hosting, API subscriptions, and more</p>
                  {canEditCosts && (
                    <Button onClick={() => setIsAddCostOpen(true)} className="mt-4 bg-slate-900 hover:bg-slate-800">
                      <Plus size={16} className="mr-1" />
                      Add First Cost
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <ProjectAnalytics 
            project={project}
            getAuthHeaders={getAuthHeaders}
            onProjectUpdate={(updatedProject) => setProject(updatedProject)}
          />
        </TabsContent>

        {/* Media Tab */}
        <TabsContent value="media" className="space-y-6">
          <MediaBrowser
            tenantId={project?.organization_id}
            clientId={project?.client_id}
            projectId={projectId}
            getAuthHeaders={getAuthHeaders}
            canCreateFolders={!isClientTeam}
            canDeleteFolders={user?.role === 'super_admin' || user?.role === 'admin'}
            canDeleteFiles={canDelete}
            title="Project Files"
          />
        </TabsContent>

        {/* Secure Logins Tab - Admin Only */}
        <TabsContent value="logins" className="space-y-6">
          <SecureLoginsFolder
            projectId={projectId}
            getAuthHeaders={getAuthHeaders}
            canAccess={user?.role === 'super_admin' || user?.role === 'admin'}
          />
        </TabsContent>

        {/* SEO Audit Tab */}
        <TabsContent value="seo-audit" className="space-y-6">
          <SEOAuditBot 
            projectId={projectId}
            projectName={project.name}
            domain={project?.website_url || client?.website || ''}
            tenantName={tenant?.name}
            getAuthHeaders={getAuthHeaders}
          />
        </TabsContent>

        {/* SEO Tab */}
        <TabsContent value="seo" className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-slate-900">SEO Analytics</h3>
            <Button onClick={() => setIsAddSeoOpen(true)} className="bg-slate-900 hover:bg-slate-800">
              <Plus size={16} className="mr-2" />
              Add SEO Data
            </Button>
          </div>

          {seoData.length > 0 ? (
            <>
              {/* SEO Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="border-slate-200 shadow-sm">
                  <CardContent className="p-4 text-center">
                    <Target size={24} className="mx-auto text-blue-600 mb-2" />
                    <p className="text-2xl font-bold text-slate-900">{latestSeo?.keywords?.length || 0}</p>
                    <p className="text-xs text-slate-500">Keywords</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200 shadow-sm">
                  <CardContent className="p-4 text-center">
                    <Globe size={24} className="mx-auto text-emerald-600 mb-2" />
                    <p className="text-2xl font-bold text-slate-900">{latestSeo?.domain_authority || 0}</p>
                    <p className="text-xs text-slate-500">Domain Authority</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200 shadow-sm">
                  <CardContent className="p-4 text-center">
                    <TrendingUp size={24} className="mx-auto text-purple-600 mb-2" />
                    <p className="text-2xl font-bold text-slate-900">{latestSeo?.organic_traffic?.toLocaleString() || 0}</p>
                    <p className="text-xs text-slate-500">Organic Traffic</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200 shadow-sm">
                  <CardContent className="p-4 text-center">
                    <Link size={24} className="mx-auto text-amber-600 mb-2" />
                    <p className="text-2xl font-bold text-slate-900">{latestSeo?.backlinks?.toLocaleString() || 0}</p>
                    <p className="text-xs text-slate-500">Backlinks</p>
                  </CardContent>
                </Card>
              </div>

              {/* Charts */}
              {chartData.length > 1 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card className="border-slate-200 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-base">Authority Trend</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip />
                          <Bar dataKey="da" fill="#0f172a" name="DA" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="pa" fill="#3b82f6" name="PA" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card className="border-slate-200 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-base">Traffic & Backlinks</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip />
                          <Line type="monotone" dataKey="traffic" stroke="#10b981" strokeWidth={2} />
                          <Line type="monotone" dataKey="backlinks" stroke="#f59e0b" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Keywords */}
              {latestSeo?.keywords?.length > 0 && (
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-base">Target Keywords</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {latestSeo.keywords.map((kw, i) => (
                        <span key={i} className="px-3 py-1 bg-slate-100 rounded-full text-sm text-slate-700">
                          {kw}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-12 text-center">
                <BarChart3 size={48} className="mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700">No SEO data yet</h3>
                <p className="text-slate-500 mt-1">Add SEO metrics to track this project&apos;s performance</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-slate-900">Project Invoices</h3>
            <Button onClick={() => navigate('/invoices')} variant="outline">
              View All Invoices
            </Button>
          </div>

          {invoices.length > 0 ? (
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-0">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Invoice #</th>
                      <th>Amount</th>
                      <th>Due Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td className="font-medium">{invoice.invoice_number}</td>
                        <td>${invoice.total.toLocaleString()}</td>
                        <td>{format(new Date(invoice.due_date), 'MMM d, yyyy')}</td>
                        <td>
                          <span className={`status-badge invoice-${invoice.status}`}>
                            {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-12 text-center">
                <FileText size={48} className="mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700">No invoices yet</h3>
                <p className="text-slate-500 mt-1">Create an invoice for this project from the Invoices page</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Project Modal */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Edit Project</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateProject} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Project Name *</Label>
              <Input
                value={projectForm.name}
                onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={projectForm.description}
                onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={projectForm.status}
                  onValueChange={(value) => setProjectForm({ ...projectForm, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Budget ($)</Label>
                <Input
                  type="number"
                  value={projectForm.budget}
                  onChange={(e) => setProjectForm({ ...projectForm, budget: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Project Live Website URL</Label>
              <Input
                type="url"
                value={projectForm.website_url}
                onChange={(e) => setProjectForm({ ...projectForm, website_url: e.target.value })}
                placeholder="https://example.com"
                data-testid="project-website-url-input"
              />
              <p className="text-xs text-slate-500">Used as the default domain for SEO audits</p>
            </div>
            {/* Dev Site URL - Super Admin Only */}
            {user?.role === 'super_admin' && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Monitor size={14} className="text-emerald-600" />
                  Development Site URL
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">Admin Only</span>
                </Label>
                <Input
                  type="url"
                  value={projectForm.dev_site_url}
                  onChange={(e) => setProjectForm({ ...projectForm, dev_site_url: e.target.value })}
                  placeholder="https://dev.example.com"
                  data-testid="project-dev-site-url-input"
                />
                <p className="text-xs text-slate-500">Tenants and clients will see a &quot;See Site&quot; button instead of this URL</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon size={16} className="mr-2" />
                    {dueDate ? format(dueDate, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={dueDate} onSelect={setDueDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            {/* Custom Fields based on Project Type */}
            {renderEditCustomFields()}

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">
                Update Project
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add SEO Data Modal */}
      <Dialog open={isAddSeoOpen} onOpenChange={setIsAddSeoOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Add SEO Data</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddSeoData} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Keywords (comma separated)</Label>
              <Input
                value={seoForm.keywords}
                onChange={(e) => setSeoForm({ ...seoForm, keywords: e.target.value })}
                placeholder="seo services, web design, marketing"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Domain Authority (0-100)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={seoForm.domain_authority}
                  onChange={(e) => setSeoForm({ ...seoForm, domain_authority: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Page Authority (0-100)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={seoForm.page_authority}
                  onChange={(e) => setSeoForm({ ...seoForm, page_authority: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Organic Traffic</Label>
                <Input
                  type="number"
                  value={seoForm.organic_traffic}
                  onChange={(e) => setSeoForm({ ...seoForm, organic_traffic: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Backlinks</Label>
                <Input
                  type="number"
                  value={seoForm.backlinks}
                  onChange={(e) => setSeoForm({ ...seoForm, backlinks: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={seoForm.notes}
                onChange={(e) => setSeoForm({ ...seoForm, notes: e.target.value })}
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsAddSeoOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">
                Add Data
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Task Modal */}
      <Dialog open={isAddTaskOpen} onOpenChange={setIsAddTaskOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Add Task</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Task Title *</Label>
              <Input
                value={taskForm.title}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                placeholder="e.g., Complete homepage design"
                required
                data-testid="task-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                placeholder="Task details..."
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Assign To ({taskForm.assigned_to?.length || 0} selected)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <UserPlus size={16} className="mr-2" />
                      {taskForm.assigned_to?.length > 0 
                        ? `${taskForm.assigned_to.length} assignee(s)` 
                        : 'Select assignees'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-2">
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {assignableUsers.map(u => (
                        <div 
                          key={u.id} 
                          className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer"
                          onClick={() => toggleAssignee(u.id)}
                        >
                          <Checkbox 
                            checked={(taskForm.assigned_to || []).includes(u.id)}
                            className="pointer-events-none"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-800 truncate">{u.name}</p>
                            <p className="text-xs text-slate-400">{u.type}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={taskForm.priority}
                  onValueChange={(value) => setTaskForm({ ...taskForm, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon size={16} className="mr-2" />
                    {taskDueDate ? format(taskDueDate, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={taskDueDate} onSelect={setTaskDueDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
              <Checkbox
                checked={taskForm.is_milestone}
                onCheckedChange={(checked) => setTaskForm({ ...taskForm, is_milestone: checked })}
                id="is_milestone"
              />
              <Label htmlFor="is_milestone" className="text-sm font-medium text-purple-700 cursor-pointer">
                Mark as Milestone
              </Label>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsAddTaskOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-task-btn">
                Create Task
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Task Modal with Comments */}
      <Dialog open={isEditTaskOpen} onOpenChange={(open) => {
        if (!open) {
          setSelectedTask(null);
          setTaskComments([]);
          setNewComment('');
          setTaskForm({ title: '', description: '', assigned_to: [], priority: 'medium', is_milestone: false });
          setTaskDueDate(null);
        }
        setIsEditTaskOpen(open);
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Edit Task</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleUpdateTask} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Task Title *</Label>
              <Input
                value={taskForm.title}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                placeholder="e.g., Complete homepage design"
                required
                data-testid="edit-task-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                placeholder="Task details..."
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Assign To ({taskForm.assigned_to?.length || 0} selected)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <UserPlus size={16} className="mr-2" />
                      {taskForm.assigned_to?.length > 0 
                        ? `${taskForm.assigned_to.length} assignee(s)` 
                        : 'Select assignees'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-2">
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {assignableUsers.map(u => (
                        <div 
                          key={u.id} 
                          className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer"
                          onClick={() => toggleAssignee(u.id)}
                        >
                          <Checkbox 
                            checked={(taskForm.assigned_to || []).includes(u.id)}
                            className="pointer-events-none"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-800 truncate">{u.name}</p>
                            <p className="text-xs text-slate-400">{u.type}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={taskForm.priority}
                  onValueChange={(value) => setTaskForm({ ...taskForm, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon size={16} className="mr-2" />
                    {taskDueDate ? format(taskDueDate, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={taskDueDate} onSelect={setTaskDueDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
              <Checkbox
                checked={taskForm.is_milestone}
                onCheckedChange={(checked) => setTaskForm({ ...taskForm, is_milestone: checked })}
                id="edit_is_milestone"
              />
              <Label htmlFor="edit_is_milestone" className="text-sm font-medium text-purple-700 cursor-pointer">
                Mark as Milestone
              </Label>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsEditTaskOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="update-task-btn">
                Update Task
              </Button>
            </div>
          </form>

          {/* Comments Section */}
          <div className="border-t border-slate-200 mt-6 pt-6">
            <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-4">
              <MessageSquare size={16} />
              Comments ({taskComments.length})
            </h4>
            
            {/* Comment List */}
            {taskComments.length > 0 ? (
              <div className="space-y-3 max-h-60 overflow-y-auto mb-4">
                {taskComments.map((comment) => (
                  <div key={comment.id} className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-6 h-6 bg-slate-300 rounded-full flex items-center justify-center text-xs font-medium text-slate-700">
                          {comment.user_name?.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-sm font-medium text-slate-900">{comment.user_name}</span>
                        <span className="text-xs text-slate-400">
                          {format(new Date(comment.created_at), 'MMM d, h:mm a')}
                        </span>
                        {comment.updated_at && (
                          <span className="text-xs text-slate-400 italic">(edited)</span>
                        )}
                      </div>
                      {(comment.user_id === user?.id || user?.role === 'super_admin') && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-red-400 hover:text-red-600"
                          onClick={() => handleDeleteComment(comment.id)}
                        >
                          <X size={14} />
                        </Button>
                      )}
                    </div>
                    <p className="text-sm text-slate-700 ml-8">{comment.content}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 mb-4">No comments yet</p>
            )}
            
            {/* Add Comment */}
            <div className="flex gap-2">
              <Input
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleAddComment()}
                data-testid="task-comment-input"
              />
              <Button 
                type="button"
                onClick={handleAddComment}
                disabled={!newComment.trim()}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="add-comment-btn"
              >
                <Send size={16} />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dev Site Preview - Popup Modal with hidden URL */}
      {isDevSiteOpen && (
        <div className="fixed inset-0 z-[100] bg-black/90" data-testid="dev-site-popup">
          <div className="absolute top-0 left-0 right-0 h-12 bg-slate-900 border-b border-slate-700 flex items-center justify-between px-4">
            <div className="flex items-center gap-2 text-white">
              <Monitor size={18} className="text-emerald-400" />
              <span className="font-medium">Development Site Preview</span>
              <span className="text-slate-400 text-sm">— {project?.name}</span>
            </div>
            <button
              onClick={() => setIsDevSiteOpen(false)}
              className="text-white hover:bg-slate-700 px-3 py-1 rounded flex items-center gap-1"
              data-testid="close-dev-preview-btn"
            >
              <X size={16} />
              Close
            </button>
          </div>
          <div className="absolute top-12 left-0 right-0 bottom-0 bg-white">
            <iframe
              src={devSiteUrl}
              className="w-full h-full border-0"
              title="Development Site Preview"
              data-testid="dev-site-iframe"
            />
          </div>
        </div>
      )}

      {/* Add Cost Dialog */}
      <Dialog open={isAddCostOpen} onOpenChange={setIsAddCostOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Project Cost</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddCost} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Cost Name *</Label>
              <Input
                value={costForm.name}
                onChange={(e) => setCostForm({ ...costForm, name: e.target.value })}
                placeholder="e.g., AWS Hosting, OpenAI API, Twilio SMS"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={costForm.description}
                onChange={(e) => setCostForm({ ...costForm, description: e.target.value })}
                placeholder="Optional details about this cost"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={costForm.amount}
                  onChange={(e) => setCostForm({ ...costForm, amount: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Frequency *</Label>
                <Select
                  value={costForm.frequency}
                  onValueChange={(value) => setCostForm({ ...costForm, frequency: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_time">One-Time</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select
                value={costForm.category}
                onValueChange={(value) => setCostForm({ ...costForm, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hosting">Hosting</SelectItem>
                  <SelectItem value="api">API / Service</SelectItem>
                  <SelectItem value="subscription">Subscription</SelectItem>
                  <SelectItem value="credits">Credits</SelectItem>
                  <SelectItem value="messaging">Messaging (SMS/Email)</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsAddCostOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">Add Cost</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Cost Dialog */}
      <Dialog open={isEditCostOpen} onOpenChange={setIsEditCostOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Cost</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateCost} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Cost Name *</Label>
              <Input
                value={costForm.name}
                onChange={(e) => setCostForm({ ...costForm, name: e.target.value })}
                placeholder="e.g., AWS Hosting, OpenAI API, Twilio SMS"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={costForm.description}
                onChange={(e) => setCostForm({ ...costForm, description: e.target.value })}
                placeholder="Optional details about this cost"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={costForm.amount}
                  onChange={(e) => setCostForm({ ...costForm, amount: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Frequency *</Label>
                <Select
                  value={costForm.frequency}
                  onValueChange={(value) => setCostForm({ ...costForm, frequency: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_time">One-Time</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select
                value={costForm.category}
                onValueChange={(value) => setCostForm({ ...costForm, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hosting">Hosting</SelectItem>
                  <SelectItem value="api">API / Service</SelectItem>
                  <SelectItem value="subscription">Subscription</SelectItem>
                  <SelectItem value="credits">Credits</SelectItem>
                  <SelectItem value="messaging">Messaging (SMS/Email)</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsEditCostOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetail;
